/* AlertAudio.service.js
 * Encapsulates Web Audio API usage: shared AudioContext, unlock handling,
 * prompt registration, and playing one-or-more prompts.
 */
'use strict';

angular.module('App.v1.Services')
    .factory('App.v1.Services.AlertAudio', ['$log', '$window', function ($log, $window) {
        var _audioCtx = null;
        var _prompts = {};
        var _pending = [];
        var _unlockHandlerAdded = false;

        // Register built-in prompts at load time so callers don't need to.
        // ConversationUpdate: two-tone ping used for new conversation updates.
        _prompts['ConversationUpdate'] = [
            { freq: 440.0, start: 0, decay: 0.13, gain: 1.0, type: 'sine' },
            { freq: 739.99, start: 0.15, decay: 0.10, gain: 0.85, type: 'sine' }
        ];

        function getAudioContext() {
            if (_audioCtx) return _audioCtx;
            var AC = $window.AudioContext || $window.webkitAudioContext;
            if (!AC) return null;
            try {
                _audioCtx = new AC();
            } catch (e) {
                return null;
            }
            return _audioCtx;
        }

        function initUnlockHandler() {
            if (_unlockHandlerAdded) return;
            _unlockHandlerAdded = true;

            var resume = function () {
                try {
                    var ctx = getAudioContext();
                    if (ctx && ctx.state === 'suspended') {
                        ctx.resume().catch(function () { /* ignore */ });
                    }
                } catch (e) { /* ignore */ }

                $window.document.removeEventListener('click', resume, true);
                $window.document.removeEventListener('touchstart', resume, true);

                // Play any pending prompts once unlocked
                while (_pending.length) {
                    var p = _pending.shift();
                    play(p);
                }
            };

            $window.document.addEventListener('click', resume, true);
            $window.document.addEventListener('touchstart', resume, true);
        }

        function registerPrompt(name, notes) {
            _prompts[name] = notes;
        }

        // Expose a global helper so external shortcuts or debug tooling can
        // trigger audio without reaching into Angular. This explicitly
        // plays the requested prompt and does NOT respect any snooze state.
        try {
            $window.playSound = function (promptName) {
                var name = promptName || 'ConversationUpdate';
                try {
                    play(name);
                } catch (e) {
                    $log.error('playSound error', e);
                }
            };
        } catch (e) {
            // ignore if window isn't writable in some envs
        }

        function play(promptName) {
            // Ensure unlock handlers exist before attempting to create/resume context
            initUnlockHandler();

            var ctx = getAudioContext();
            if (!ctx) return;

            var notes = _prompts[promptName] || [];

            var _playNow = function () {
                try {
                    var now = ctx.currentTime;

                    notes.forEach(function (note) {
                        var osc = ctx.createOscillator();
                        var gain = ctx.createGain();

                        osc.type = note.type || 'sine';
                        osc.frequency.setValueAtTime(note.freq || 440, now + (note.start || 0));

                        var startTime = now + (note.start || 0);
                        gain.gain.setValueAtTime(0.0001, startTime);
                        var attack = (note.attack !== undefined) ? note.attack : 0.003;
                        gain.gain.linearRampToValueAtTime((note.gain || 1) * 0.8, startTime + attack);
                        gain.gain.exponentialRampToValueAtTime(0.0001, startTime + (note.decay || 0.13));

                        osc.connect(gain);
                        gain.connect(ctx.destination);

                        osc.start(startTime);
                        osc.stop(startTime + (note.decay || 0.13) + ((note.release !== undefined) ? note.release : 0.025));
                    });
                } catch (e) {
                    $log.error('AlertAudio play error', e);
                }
            };

            // If suspended (Safari), try to resume first; if resume fails, queue
            if (ctx.state === 'suspended') {
                ctx.resume().then(function () {
                    _playNow();
                }).catch(function () {
                    _pending.push(promptName);
                    // unlock handler already added above via initUnlockHandler()
                });
            } else {
                _playNow();
            }
        }

        // Expose API. `init()` is lightweight: it only attaches the unlock
        // handlers and is safe to call during app bootstrap if you prefer
        // explicit initialization. The service still works lazily if you
        // never call `init()` because `play()` will ensure handlers exist.
        function init() {
            initUnlockHandler();
        }

        return {
            init: init,
            registerPrompt: registerPrompt,
            play: play
        };
    }]);
